import { Loader2, Plus, Search, Trash, Upload } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import {
  useAddWhitelist,
  useDeleteWhitelist,
  useImportWhitelist,
  useWhitelist,
} from "@/entities/whitelist/queries";
import type { WhitelistImportResult } from "@/entities/whitelist/types";
import { ApiError } from "@/shared/api/http";
import { cn } from "@/shared/lib/cn";
import { formatDate } from "@/shared/lib/format";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/ui/alert-dialog";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Skeleton } from "@/shared/ui/skeleton";

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function AdminWhitelistTable(): JSX.Element {
  const [q, setQ] = React.useState("");
  const debouncedQ = useDebounced(q, 300);
  const { data, isLoading, isError } = useWhitelist({
    q: debouncedQ || undefined,
    limit: 200,
    offset: 0,
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск по email…"
            className="h-9 min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-muted-foreground"
          />
        </div>
        <AddManuallyDialog />
        <ImportCsvDialog />
      </div>

      {isLoading ? (
        <SkeletonTable />
      ) : isError || !data ? (
        <div className="glass-card p-6 text-sm text-pink">
          Не удалось загрузить whitelist.
        </div>
      ) : data.items.length === 0 ? (
        <div className="glass-card p-12 text-center text-sm text-muted-foreground">
          {debouncedQ ? "Ничего не найдено" : "Whitelist пуст. Добавьте email вручную или импортируйте CSV."}
        </div>
      ) : (
        <div className="glass-card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03]">
              <tr className="border-b border-white/10">
                <Th>Email</Th>
                <Th>Примечание</Th>
                <Th>Добавлено</Th>
                <Th className="w-12 text-right" />
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <Row key={it.email} email={it.email} note={it.note} addedAt={it.added_at} />
              ))}
            </tbody>
          </table>
          <div className="border-t border-white/10 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground">
            Показано {data.items.length} из {data.total}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({
  email,
  note,
  addedAt,
}: {
  email: string;
  note: string | null;
  addedAt: string;
}): JSX.Element {
  const del = useDeleteWhitelist();
  return (
    <tr className="border-b border-white/10 last:border-0">
      <td className="px-3 py-2.5 align-middle font-mono text-xs text-foreground/95">
        {email}
      </td>
      <td className="px-3 py-2.5 align-middle text-sm text-foreground/85">
        {note ?? <span className="text-muted-foreground">—</span>}
      </td>
      <td className="px-3 py-2.5 align-middle text-xs text-muted-foreground">
        {formatDate(addedAt)}
      </td>
      <td className="px-3 py-2.5 text-right">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-pink/40 bg-white/[0.04] text-pink hover:text-pink"
              disabled={del.isPending}
            >
              {del.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash className="h-3.5 w-3.5" />}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Убрать {email} из whitelist?</AlertDialogTitle>
              <AlertDialogDescription>
                Уже зарегистрированных юзеров это не затронет. Новые с этим email будут попадать в очередь.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Отмена</AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  try {
                    await del.mutateAsync(email);
                    toast.success("Удалено");
                  } catch (e) {
                    toast.error(e instanceof ApiError ? e.message : "Не удалось удалить");
                  }
                }}
              >
                Удалить
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </td>
    </tr>
  );
}

function AddManuallyDialog(): JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [note, setNote] = React.useState("");
  const add = useAddWhitelist();
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="border-white/15 bg-white/[0.04]"
        >
          <Plus className="h-3.5 w-3.5" /> Добавить вручную
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Добавить email в whitelist</DialogTitle>
          <DialogDescription>
            После добавления юзер с таким email будет одобрен сразу при регистрации.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              autoFocus
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Примечание (опционально)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="курс январь 2026"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="border-white/15 bg-white/[0.04]"
            onClick={() => setOpen(false)}
            disabled={add.isPending}
          >
            Отмена
          </Button>
          <Button
            type="button"
            className="bg-gradient-primary text-white shadow-glow-purple"
            disabled={add.isPending || !email.trim()}
            onClick={async () => {
              try {
                await add.mutateAsync({ email: email.trim(), note: note.trim() || undefined });
                toast.success("Добавлено в whitelist");
                setEmail("");
                setNote("");
                setOpen(false);
              } catch (e) {
                if (e instanceof ApiError && e.code === "EMAIL_TAKEN") {
                  toast.error("Этот email уже в whitelist");
                } else {
                  toast.error(e instanceof ApiError ? e.message : "Не удалось добавить");
                }
              }
            }}
          >
            {add.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportCsvDialog(): JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [file, setFile] = React.useState<File | null>(null);
  const [result, setResult] = React.useState<WhitelistImportResult | null>(null);
  const [errorsOpen, setErrorsOpen] = React.useState(false);
  const importMut = useImportWhitelist();

  const reset = () => {
    setFile(null);
    setResult(null);
    setErrorsOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button
          type="button"
          className="bg-gradient-primary text-white shadow-glow-purple"
        >
          <Upload className="h-3.5 w-3.5" /> Импорт CSV
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Импорт whitelist из CSV</DialogTitle>
          <DialogDescription>
            Минимум — одна колонка <code>email</code>. Опционально — две: <code>email,note</code>.
            Кодировка <code>utf-8</code>. Лимит 2 МБ.
          </DialogDescription>
        </DialogHeader>

        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            setFile(f);
            setResult(null);
          }}
          className="block w-full rounded-md border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-foreground file:mr-3 file:rounded-md file:border-0 file:bg-purple/15 file:px-3 file:py-1 file:text-sm file:font-medium file:text-purple"
        />

        {result && (
          <div className="rounded-lg border border-white/10 bg-white/[0.04] p-4 text-sm">
            <div className="grid grid-cols-3 gap-3 text-center">
              <Stat label="Добавлено" value={result.added} color="green" />
              <Stat label="Пропущено" value={result.skipped} color="amber" />
              <Stat label="Бэкфилл юзеров" value={result.backfilled} color="cyan" />
            </div>
            {result.errors.length > 0 && (
              <details
                open={errorsOpen}
                onToggle={(e) => setErrorsOpen((e.target as HTMLDetailsElement).open)}
                className="mt-3 rounded-md border border-pink/30 bg-pink/[0.06] p-3"
              >
                <summary className="cursor-pointer text-sm font-semibold text-pink">
                  Ошибки: {result.errors.length}
                </summary>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-foreground/85">
                  {result.errors.map((er, i) => (
                    <li key={i}>{er}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="border-white/15 bg-white/[0.04]"
            onClick={() => setOpen(false)}
            disabled={importMut.isPending}
          >
            Закрыть
          </Button>
          <Button
            type="button"
            className="bg-gradient-primary text-white shadow-glow-purple"
            disabled={!file || importMut.isPending}
            onClick={async () => {
              if (!file) return;
              try {
                const r = await importMut.mutateAsync(file);
                setResult(r);
                if (r.errors.length === 0) toast.success("Импорт завершён");
                else toast.warning(`Импорт завершён, ${r.errors.length} ошибок`);
              } catch (e) {
                if (e instanceof ApiError && e.code === "IMPORT_FILE_TOO_LARGE") {
                  toast.error("Файл больше 2 МБ");
                } else {
                  toast.error(e instanceof ApiError ? e.message : "Не удалось импортировать");
                }
              }
            }}
          >
            {importMut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Загрузить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: "green" | "amber" | "cyan";
}): JSX.Element {
  return (
    <div className="rounded-md border border-white/10 bg-white/[0.04] p-2">
      <div
        className={cn(
          "font-mono text-xl font-bold tabular-nums",
          color === "green" && "text-green",
          color === "amber" && "text-amber",
          color === "cyan" && "text-cyan",
        )}
      >
        {value}
      </div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }): JSX.Element {
  return (
    <th
      className={cn(
        "px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground",
        className,
      )}
    >
      {children}
    </th>
  );
}

function SkeletonTable(): JSX.Element {
  return (
    <div className="space-y-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
